import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import { OptionId } from "./Option.ts"

export const minMax = (min: number, max: number) => S.Int.pipe(S.check(S.isBetween({ minimum: min, maximum: max })))

const OptionConfig = S.UndefinedOr(
  S.Struct({
    focusedBy: S.optionalKey(S.Array(OptionId)),
    controlledBy: S.optionalKey(OptionId)
  })
)

// `focusedBy` is load-bearing: runtime `findFocusIdInObject` (Embed.vue) treats any node with
// a `focusedBy` array as a focus target and uses its path as the focus key (e.g. `showerTray.left`).
// So each variant must be `{ focusedBy }`, not a bare array. Like OptionConfig, minus `controlledBy`.
const FocusConfig = S.UndefinedOr(
  S.Struct({
    focusedBy: S.optionalKey(S.Array(OptionId))
  })
)

const defaultPresentation = {
  mask: true,
  lightning: "#d2d2ff"
} as const

const defaultGlassArtBackgroundSize = {
  width: 100,
  height: 100
} as const

export const GlassColor = S.Struct({
  controlledBy: S.optionalKey(OptionId),
  color: S.String.pipe(
    S.withDecodingDefaultKey(Effect.succeed("#000000"))
  ),
  opacity: S.Finite.pipe(
    S.check(S.isBetween({ minimum: 0, maximum: 100 })),
    S.withDecodingDefaultKey(Effect.succeed(100))
  ),
  satination: S.Finite.pipe(
    S.check(S.isBetween({ minimum: 0, maximum: 10 })),
    S.withDecodingDefaultKey(Effect.succeed(10))
  ),
  mirrored: S.Boolean.pipe(S.withDecodingDefaultKey(Effect.succeed(false)))
})

// Shared value schemas for the previewer axes. The live preview inputs
// (resources/Configuration.ts ShowerPreviewInputs) reuse these, so the
// backoffice defaults below and the runtime inputs cannot drift apart.
export const DoorAlignment = S.Literals(["left", "right", "center"])
export const DoorMechanismValue = S.Literals(["hinged", "sliding"])
export const HandleValue = S.Literals(["knob", "bar", "flush"])
export const BlocksValue = S.Literals(["profile", "block", "profile-on-wall"])
export const PanelShapeValue = S.Literals(["rectangular", "sloped"])
export const HiddenSideValue = S.Literals(["none", "left", "right"])
export const GlassSize = minMax(200, 5000)
export const DoorWidthValue = S.Union([
  GlassSize,
  S.Struct({
    left: minMax(0, 5000),
    right: minMax(0, 5000)
  })
])
// Plain scalar (no `0 | minMax` union): 0 is already inside the range, and
// OmegaForm renders literal unions as a select instead of a number input
export const ShowerTrayHeightValue = minMax(0, 5000)

// Backoffice-configurable fallback values: a fully-optional mirror of
// ShowerPreviewInputs. Every unset field keeps today's hardcoded fallback.
// `sides` and `presentation` are not here: they are plain model fields with
// their own backoffice controls, so they never fall back.
export const ShowerPreviewerDefaults = S.Struct({
  showerTray: S.optional(S.Struct({
    exist: S.optional(S.Boolean),
    height: S.optional(ShowerTrayHeightValue)
  })),
  width: S.optional(S.Struct({
    left: S.optional(GlassSize),
    right: S.optional(GlassSize)
  })),
  height: S.optional(S.Struct({
    left: S.optional(GlassSize),
    right: S.optional(GlassSize),
    internal: S.optional(GlassSize)
  })),
  topWidth: S.optional(S.Struct({
    left: S.optional(minMax(0, 5000)),
    right: S.optional(minMax(0, 5000))
  })),
  panelShape: S.optional(PanelShapeValue),
  hiddenSide: S.optional(HiddenSideValue),
  detached: S.optional(S.Boolean),
  doors: S.optional(S.Struct({
    position: S.optional(DoorAlignment),
    fixedPosition: S.optional(DoorAlignment),
    open: S.optional(DoorAlignment),
    quantity: S.optional(minMax(1, 2)),
    // scalar only (no per-side {left, right} variant): the backoffice edits a
    // single Türbreite, and OmegaForm renders no input for optional unions
    width: S.optional(GlassSize),
    fullWidth: S.optional(S.Boolean),
    mechanism: S.optional(DoorMechanismValue)
  })),
  fittings: S.optional(S.Struct({
    // Plain String (not NonEmptyString): the backoffice ColorPicker registers
    // its field as "" on mount; consumers treat "" as unset.
    color: S.optional(S.String),
    handle: S.optional(HandleValue),
    blocks: S.optional(BlocksValue)
  })),
  glass: S.optional(S.Struct({
    color: S.optional(S.Struct({
      // Plain String for the same ColorPicker reason as fittings.color above
      code: S.optional(S.String),
      transparency: S.optional(minMax(0, 100)),
      satin: S.optional(minMax(0, 10)),
      mirrored: S.optional(S.Boolean)
    }))
  }))
})

// TODO: How can I enforce a connection between a prop and a specific type of option?
export class ShowerPreviewer extends S.Opaque<ShowerPreviewer, ShowerPreviewer.Encoded>()(
  S
    .Struct({
      enabled: S.Boolean.withConstructorDefault,
      height: S
        .NullOr(
          S.Struct({
            left: S.optionalKey(OptionConfig),
            right: S.optionalKey(OptionConfig),
            internal: S.optionalKey(OptionConfig)
          })
        )
        .withConstructorDefault,
      width: S
        .NullOr(
          S.Struct({
            left: S.optionalKey(OptionConfig),
            right: S.optionalKey(OptionConfig)
          })
        )
        .withConstructorDefault,
      sides: minMax(1, 2).pipe(S.withConstructorDefault(Effect.succeed(2))),
      showerTray: S
        .NullOr(
          S.Struct({
            exists: S.optionalKey(OptionId),
            active: S.optionalKey(OptionId),
            height: S.optionalKey(OptionId),
            left: S.optionalKey(FocusConfig),
            right: S.optionalKey(FocusConfig),
            internal: S.optionalKey(FocusConfig)
          })
        )
        .withConstructorDefault,
      doors: S
        .NullOr(
          S.Struct({
            quantity: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: minMax(1, 2)
              }))
            })))),
            open: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Union([S.Literal("left"), S.Literal("right"), S.Literal("not-set")])
              }))
            })))),
            position: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Union([S.Literal("left"), S.Literal("center"), S.Literal("right"), S.Literal("not-set")])
              }))
            })))),
            fixedPosition: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Union([S.Literal("left"), S.Literal("center"), S.Literal("right"), S.Literal("not-set")])
              }))
            })))),
            fixedPositionFocusedBy: S.optionalKey(S.Array(OptionId)),
            fixedPositionFocusedByLeft: S.optionalKey(S.Array(OptionId)),
            fixedPositionFocusedByRight: S.optionalKey(S.Array(OptionId)),
            oppositeFocusedBy: S.optionalKey(S.Array(OptionId)),
            fixedLeftFocusedBy: S.optionalKey(S.Array(OptionId)),
            fixedRightFocusedBy: S.optionalKey(S.Array(OptionId)),
            width: S.optionalKey(S.Struct({
              main: S.optionalKey(OptionConfig),
              left: S.optionalKey(OptionConfig),
              right: S.optionalKey(OptionConfig)
            })),
            fullWidth: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              active: S.mutable(S.Array(OptionId))
            })))),
            defaultWidth: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Finite
              }))
            })))),
            mechanism: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Union([S.Literal("hinged"), S.Literal("sliding"), S.Literal("not-set")])
              }))
            }))))
          })
        )
        .withConstructorDefault,
      presentation: S
        .Struct({
          mask: S.Boolean.pipe(S.withDecodingDefaultKey(Effect.succeed(defaultPresentation.mask))),
          lightning: S.String.pipe(S.withDecodingDefaultKey(Effect.succeed(defaultPresentation.lightning)))
        })
        .pipe(S.withConstructorDefault(Effect.succeed(defaultPresentation))),
      glass: S
        .NullOr(S.Struct({
          arts: S.optionalKey(S.Struct({
            controlledBy: S.optionalKey(OptionId),
            items: S.NonEmptyArray(S.Struct({
              controlledBy: OptionId,
              imageUrl: S.NullOr(S.String).pipe(S.withDecodingDefaultKey(Effect.succeed(null))),
              horizontalPosition: S
                .Union([S.Literal("left"), S.Literal("center"), S.Literal("right")])
                .pipe(S.withDecodingDefaultKey(Effect.succeed("center" as const))),
              verticalPosition: S
                .Union([S.Literal("top"), S.Literal("center"), S.Literal("bottom")])
                .pipe(S.withDecodingDefaultKey(Effect.succeed("center" as const))),
              backgroundRepeat: S
                .Union([
                  S.Literal("repeat"),
                  S.Literal("repeat-x"),
                  S.Literal("repeat-y"),
                  S.Literal("no-repeat")
                ])
                .pipe(S.withDecodingDefaultKey(Effect.succeed("no-repeat" as const))),
              backgroundSize: S
                .Struct({
                  width: minMax(0, 100),
                  height: minMax(0, 100)
                })
                .pipe(S.withDecodingDefaultKey(Effect.succeed(defaultGlassArtBackgroundSize)))
            }))
          })),
          colors: S.optionalKey(S.Struct({
            items: S.NonEmptyArray(GlassColor),
            controlledBy: S.optionalKey(OptionId)
          }))
        }))
        .withConstructorDefault,
      fittings: S
        .NullOr(
          S.Struct({
            color: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.NonEmptyString255
              }))
            })))),
            handle: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Union([S.Literal("knob"), S.Literal("bar"), S.Literal("flush"), S.Literal("not-set")])
              }))
            })))),
            blocks: S.optionalKey(S.mutable(S.Array(S.Struct({
              controlledBy: OptionId,
              items: S.NonEmptyArray(S.Struct({
                controlledBy: OptionId,
                value: S.Union([
                  S.Literal("profile"),
                  S.Literal("block"),
                  S.Literal("profile-on-wall"),
                  S.Literal("not-set")
                ])
              }))
            }))))
          })
        )
        .withConstructorDefault,
      panelShape: S.optionalKey(S.mutable(S.Array(S.Struct({
        controlledBy: OptionId,
        items: S.NonEmptyArray(S.Struct({
          controlledBy: OptionId,
          value: S.Union([S.Literal("rectangular"), S.Literal("sloped"), S.Literal("not-set")])
        }))
      })))),
      detached: S.optionalKey(S.mutable(S.Array(S.Struct({
        controlledBy: OptionId,
        items: S.NonEmptyArray(S.Struct({
          controlledBy: OptionId,
          value: S.Union([S.Literal("detached"), S.Literal("attached"), S.Literal("not-set")])
        }))
      })))),
      topWidth: S
        .NullOr(
          S.Struct({
            left: S.optionalKey(OptionConfig),
            right: S.optionalKey(OptionConfig)
          })
        )
        .withConstructorDefault,
      hiddenSide: S.optionalKey(S.mutable(S.Array(S.Struct({
        controlledBy: OptionId,
        items: S.NonEmptyArray(S.Struct({
          controlledBy: OptionId,
          value: S.Union([S.Literal("none"), S.Literal("left"), S.Literal("right"), S.Literal("not-set")])
        }))
      })))),
      defaults: S.optional(ShowerPreviewerDefaults)
    })
    .annotate({ identifier: "ShowerPreviewer" })
) {}

// codegen:start {preset: model}
//
export namespace ShowerPreviewer {
  export interface Encoded extends S.StructNestedEncoded<typeof ShowerPreviewer> {}
}
//
// codegen:end
